import { Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";

export const AboutIntro = () => (
  <section data-testid="about-intro" className="px-5 sm:px-8 pt-8 pb-12 lg:pb-16">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <p className="mono text-[11px] tracking-[0.28em] uppercase text-neutral-500 mb-6">About Novo</p>
        <h1 className="font-display font-bold tracking-tight text-neutral-50 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
          A small studio with seriously high standards.
        </h1>
        <p className="text-neutral-400 text-base sm:text-lg leading-relaxed mt-8 max-w-4xl">
          Novo Digital Studio is a web design studio creating bespoke websites for ambitious businesses — every
          project designed with care, built properly, and finished to a standard we're proud to put our name on.
        </p>
      </Reveal>
    </div>
  </section>
);

export const StudioStory = () => (
  <section data-testid="about-story" className="px-5 sm:px-8 pt-14 lg:pt-16 pb-20 lg:pb-28 border-t border-white/5">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-[5fr_7fr] gap-14 lg:gap-16 items-center">
      <Reveal>
        <p className="mono text-[11px] tracking-[0.28em] uppercase text-neutral-500 mb-6">Our Story</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-neutral-50">
          Why Novo exists.
        </h2>
        <div className="text-neutral-400 text-sm sm:text-base leading-relaxed mt-6 space-y-5">
          <p>
            Novo means new — new ideas, new beginnings, fresh digital identities. The studio was started to give
            growing businesses a better option: websites that feel considered and personal, without agency bloat or
            off-the-shelf templates.
          </p>
          <p>
            We believe a small studio can out-care and out-craft a big one. Every project gets genuine attention —
            from the first sketch to the final pixel — because we only take on work we can do properly.
          </p>
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="rounded-2xl overflow-hidden border border-white/10">
          <img
            src="/assets/novo-studio.png"
            alt="Inside the Novo studio — design work in progress"
            data-testid="about-studio-image"
            className="w-full h-auto"
            draggable="false"
          />
        </div>
      </Reveal>
    </div>
  </section>
);

const VALUES = [
  {
    n: "01",
    title: "Clarity over complexity",
    text: "If a visitor can't understand it in seconds, it doesn't ship.",
  },
  {
    n: "02",
    title: "Every pixel earns its place",
    text: "Nothing decorative without a reason — design should do a job.",
  },
  {
    n: "03",
    title: "Built to pay for itself",
    text: "A website isn't an expense. It should win you business.",
  },
  {
    n: "04",
    title: "Honest, always",
    text: "Straight answers, realistic timelines and no jargon.",
  },
];

export const Values = () => (
  <section data-testid="about-values" className="px-5 sm:px-8 py-20 lg:py-28 border-t border-white/5">
    <div className="max-w-7xl mx-auto">
      <Reveal className="mb-12 lg:mb-16">
        <p className="mono text-[11px] tracking-[0.28em] uppercase text-neutral-500 mb-5">What We Believe</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-neutral-50">
          A few things we won't compromise on.
        </h2>
      </Reveal>
      <div className="grid sm:grid-cols-2 gap-x-12">
        {VALUES.map((v, i) => (
          <Reveal key={v.n} delay={i * 0.08}>
            <div data-testid={`value-${v.n}`} className="flex gap-7 border-t border-white/5 py-8">
              <span className="mono text-sm text-neutral-600 tracking-[0.2em] pt-1">{v.n}</span>
              <div>
                <h3 className="font-display text-xl font-semibold text-neutral-100">{v.title}</h3>
                <p className="text-sm text-neutral-400 leading-relaxed mt-2">{v.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const OUTCOME_POINTS = [
  {
    title: "Designed with intent",
    text: "A good website isn't just built — it's designed with intent. Every detail considered, every choice made to suit your brand and the people you want to reach.",
  },
  {
    title: "Everything taken off your hands",
    text: "Most business owners don't have the time, patience or desire to design their own website. We handle all of it — the design decisions, the details, the polish.",
  },
  {
    title: "Judged by the outcome",
    text: "What you get is a website that looks professional, represents your brand properly and performs the way it should — one that makes the right impression from the first second.",
  },
];

export const OutcomeFocus = () => (
  <section data-testid="about-outcome" className="px-5 sm:px-8 py-20 lg:py-28 border-t border-white/5">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <p className="mono text-[11px] tracking-[0.28em] uppercase text-neutral-500 mb-5">Why Choose Novo</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-neutral-50 max-w-2xl leading-[1.15]">
          It's easy to make a website. It's harder to make one that works.
        </h2>
      </Reveal>
      <div className="grid lg:grid-cols-3 gap-10 lg:gap-14 mt-14">
        {OUTCOME_POINTS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.1}>
            <div className="border-t border-white/10 pt-7">
              <h3 className="font-display text-xl font-semibold text-neutral-100">{p.title}</h3>
              <p className="text-sm text-neutral-400 leading-relaxed mt-3">{p.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WORKING_POINTS = [
  "One point of contact, start to finish",
  "Plain-language updates at every stage — no jargon",
  "Structured feedback rounds, so projects never drag",
  "Every website launched tested, polished and documented",
];

export const HowWeWork = () => (
  <section data-testid="about-how-we-work" className="px-5 sm:px-8 py-20 lg:py-28 border-t border-white/5">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 lg:gap-20">
      <Reveal>
        <p className="mono text-[11px] tracking-[0.28em] uppercase text-neutral-500 mb-5">How We Work</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-neutral-50">
          Considered, from first call to launch day.
        </h2>
        <p className="text-neutral-400 text-sm sm:text-base leading-relaxed mt-6 max-w-md">
          Every project follows the same careful process — the same one you'd get from a much larger studio, just
          without the layers in between.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <ul>
          {WORKING_POINTS.map((point) => (
            <li
              key={point}
              className="flex items-start gap-3 border-b border-white/5 first:border-t py-5 text-sm sm:text-base text-neutral-300"
            >
              <Check size={16} className="text-neutral-500 mt-0.5 shrink-0" />
              {point}
            </li>
          ))}
        </ul>
      </Reveal>
    </div>
  </section>
);
